import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiMessageCircle, FiCpu } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { setModalState, modalViewsConst } from '../../../store/modalSlice';
import { ConversationListItem } from '../../../store/aiChatSlice';
import ChatHistory from '../panelComponents/ChatHistory';
import ScheduleHistory from '../panelComponents/ScheduleHistory';
import { type ScheduleItem, type ScheduleQuestion, type ScheduleOccurrence } from '../../../utils/scheduleTransform';

interface ConversationHistoryProps {
  currentConversationId?: string;
  onConversationSelect: (conversation: ConversationListItem) => void;
  onNewConversation: () => void;
  embedded?: boolean;
  onOverlayClose?: () => void;
  onScheduleResultSelect?: (item: ScheduleItem, question: ScheduleQuestion, occurrence: ScheduleOccurrence) => void;
  onTabChange?: (tab: 'chat' | 'agent') => void;
  activeTab?: 'chat' | 'agent';
  scheduleUnreadCount?: number;
}

const TABS = [
  { id: 'chat' as const, label: 'Chat', Icon: FiMessageCircle },
  { id: 'agent' as const, label: 'Agents', Icon: FiCpu },
];

const ConversationalHistory: React.FC<ConversationHistoryProps> = ({
  currentConversationId,
  onConversationSelect,
  onNewConversation,
  embedded = false,
  onOverlayClose,
  onScheduleResultSelect,
  onTabChange,
  activeTab: activeTabProp,
  scheduleUnreadCount = 0,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'agent'>(activeTabProp ?? 'chat');

  useEffect(() => {
    if (activeTabProp !== undefined) {
      setActiveTab(activeTabProp);
    }
  }, [activeTabProp]);

  return (
    <>
      {!embedded && (
        <>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden fixed top-20 left-4 z-50 bg-eai-surface-control shadow-eai-card rounded-full p-2 border border-eai-border"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {isOpen && (
            <div
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsOpen(false)}
            />
          )}
        </>
      )}

      <div className={embedded ? `
        flex h-full min-h-0 w-full flex-col bg-eai-surface-card
      ` : `
        fixed lg:static inset-y-0 left-0 z-40
        w-80 bg-eai-surface-sunken border-r border-eai-border
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-full
      `}>
        {/* Header */}
        <div className="flex-none border-b border-eai-border px-3 pb-3 pt-3">
          {onOverlayClose && (
            <div className="mb-2 flex items-center justify-between">
              <span className="eai-eyebrow">Conversations</span>
              <button
                onClick={onOverlayClose}
                className="eai-btn-icon"
                aria-label="Close history"
              >
                <FiX size={15} />
              </button>
            </div>
          )}

          {/* Chat / Agents — one segmented control, so the tab you are ON is the one that reads as selected */}
          <div className="eai-seg w-full">
            {TABS.map(({ id, label, Icon }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setActiveTab(id); onTabChange?.(id); }}
                  aria-current={active ? 'page' : undefined}
                  className={`eai-seg-item flex-1 ${active ? 'eai-seg-item-active' : ''}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                  {id === 'agent' && scheduleUnreadCount > 0 && (
                    <span className="ml-0.5 inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-eai-cta-primary px-1.5 font-outfit text-eai-caption text-white">
                      {scheduleUnreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={activeTab === 'chat'
              ? onNewConversation
              : () => dispatch(setModalState({ modalOpen: true, modalView: modalViewsConst.createSchedule, properties: { __modalSize: 'md' } }))}
            className="eai-btn-cta mt-2.5 w-full"
          >
            <FiPlus className="h-4 w-4 shrink-0" />
            New {activeTab === 'chat' ? 'chat' : 'agent'}
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'chat' && (
          <ChatHistory
            currentConversationId={currentConversationId}
            onConversationSelect={(conversation) => { onConversationSelect(conversation); setIsOpen(false); }}
            onNewConversation={onNewConversation}
          />
        )}
        {activeTab === 'agent' && (
          <ScheduleHistory
            hideNewButton
            onResultSelect={(item, question, occurrence) => onScheduleResultSelect?.(item, question, occurrence)}
          />
        )}
      </div>
    </>
  );
};

export default ConversationalHistory;
